import { isPaused } from './vocabulary.js';
/**
 * The job state machine — DESIGN.md §3.
 *
 * The table below is *data*. Adding a transition means adding a row, not an
 * `if`. That is what makes the whole thing enumerable: the tests iterate every
 * (status, event) pair and assert each one is either in the table or rejected,
 * so a silent no-op cannot exist.
 *
 * This module is pure. No database, no clock, no framework — `now` is passed
 * in. Everything here is testable in milliseconds.
 */
export const TRANSITION_EVENTS = [
    'submit',
    'triage',
    'dispatch',
    'assign',
    'acknowledge',
    'decline',
    'start_travel',
    'arrive',
    'begin_work',
    'complete',
    'verify',
    'close',
    'cancel',
    'hold',
    'resume',
    'reopen',
    'dispatch_failed',
    'resolve_with_incident',
];
export const ACTOR_TYPES = ['admin', 'dispatcher', 'technician', 'system'];
// ─── Guards ─────────────────────────────────────────────────────────────────
const requireReason = ({ payload }) => payload.reason && String(payload.reason).trim().length > 0
    ? null
    : 'A reason is required — it is what makes the timeline defensible later.';
/**
 * Only the assignee moves their own job along. A dispatcher who needs to
 * correct the record reassigns; they do not pretend to be the technician.
 */
const mustBeAssignee = ({ job, actor }) => {
    if (actor.type !== 'technician')
        return null;
    if (!job.assignedTechnicianId)
        return 'This job has no assignee.';
    return actor.id === job.assignedTechnicianId
        ? null
        : 'Only the assigned technician can do this.';
};
const requireAssignee = ({ job }) => job.assignedTechnicianId ? null : 'Assign a technician first.';
/**
 * Completion needs an outcome, and a real fix needs a root cause. "Fixed it" is
 * worthless six months later when the same link fails (DESIGN.md §12.3).
 */
const requireOutcome = ({ payload }) => {
    const outcome = payload.outcome;
    if (!outcome)
        return 'Record an outcome: completed, no access, no fault found, or deferred.';
    if (outcome === 'COMPLETED' && !payload.rootCauseCode) {
        return 'A completed job needs a root cause code.';
    }
    return null;
};
const requireDeclineReason = ({ payload }) => payload.declineReasonCode
    ? null
    : 'Choose a reason — the decline reasons are the most useful data we collect.';
const notAlreadyHeld = ({ job }) => isPaused(job.status) ? 'This job is already paused.' : null;
const mustKnowWhereToResume = ({ job }) => job.heldFromStatus ? null : 'Cannot resume: the previous status was not recorded.';
// ─── The table ──────────────────────────────────────────────────────────────
const ASSIGNABLE = [
    'NEW',
    'TRIAGED',
    'DISPATCHING',
    'ASSIGNED',
    'DISPATCH_FAILED',
];
const HOLDABLE = [
    'NEW',
    'TRIAGED',
    'DISPATCHING',
    'ASSIGNED',
    'EN_ROUTE',
    'ON_SITE',
    'IN_PROGRESS',
];
const CANCELLABLE = [
    'DRAFT',
    'NEW',
    'TRIAGED',
    'DISPATCHING',
    'ASSIGNED',
    'EN_ROUTE',
    'ON_SITE',
    'IN_PROGRESS',
    'PENDING_PARTS',
    'PENDING_CUSTOMER',
    'PENDING_ACCESS',
    'ON_HOLD',
    'DISPATCH_FAILED',
];
/**
 * Which single action moves a ticket from where it is into a given set of
 * statuses — or nothing, if no one action does.
 *
 * ## Why a board needs this
 *
 * Dragging a card between columns looks like setting a status, and it must not
 * be. Status in this system is the *consequence* of an event: `dispatch`,
 * `arrive`, `finish`. Writing the status directly would skip the guards that
 * make those events mean something — a job cannot be finished without a
 * report, a technician cannot accept somebody else's work — and the board
 * would become the one screen where those rules did not apply.
 *
 * So a drop asks this what event the move corresponds to, and fires that. The
 * event carries its guards with it, and a refusal comes back as a sentence
 * rather than as a card that silently springs back.
 *
 * ## Why one step and never two
 *
 * A ticket in NEW could reach ON_SITE through three events, and a board that
 * quietly fired all three would record a technician arriving somewhere they
 * were never dispatched to. The history would be a lie told in the right
 * order. If no single action gets there, the answer is nothing, and the screen
 * says which column it *can* reach.
 *
 * `actor` narrows it to what this person may actually do: a dispatcher and a
 * technician looking at the same card are offered different moves, because
 * they have different ones.
 */
/**
 * The actions a screen must collect a reason for before firing.
 *
 * Listed rather than derived. Deriving it meant running each guard against a
 * fabricated job to see whether it complained, which is clever, fragile, and
 * silently wrong the day a guard asks for something the fabrication does not
 * have. The list is three entries and a test keeps it honest — see
 * "every rule that demands a reason is on NEEDS_REASON" in the tests.
 *
 * It matters because of the board: dragging a card fires an event with no
 * chance to type anything, so an event that always refuses without a reason
 * would give a card that springs back every time and no clue why.
 */
export const NEEDS_REASON = ['hold', 'cancel', 'reopen'];
/**
 * The same question, when the caller can supply the destination.
 *
 * Returns the action *and* the destinations it would then have to choose
 * between — so a board can turn an ambiguous drop into a question rather than
 * refusing it. Dropping on "Impediment" is genuinely ambiguous: it is four
 * statuses, not one, and which of them it is is the most useful thing about
 * the hold.
 */
export function choosableActionToReach(from, targets, actor) {
    const wanted = new Set(targets);
    const rule = RULES.find((candidate) => candidate.destinations &&
        candidate.from.includes(from) &&
        candidate.destinations.some((one) => wanted.has(one)) &&
        (!actor || candidate.actors.includes(actor)));
    if (!rule?.destinations)
        return null;
    return {
        event: rule.event,
        chooseFrom: rule.destinations.filter((one) => wanted.has(one)),
    };
}
export function actionToReach(from, targets, actor) {
    const wanted = new Set(targets);
    const rule = RULES.find((candidate) => {
        /*
         * A rule whose destination is computed cannot be offered on a board.
         * Where it lands depends on the ticket — `finish` goes to WORK_DONE or
         * back to IN_PROGRESS depending on the report — so a column cannot
         * promise it, and a card that landed somewhere else than where it was
         * dropped is worse than one that would not move.
         */
        if (typeof candidate.to !== 'string')
            return false;
        if (!candidate.from.includes(from))
            return false;
        if (!wanted.has(candidate.to))
            return false;
        /*
         * A rule whose `to` is the status it came from is an acknowledgement, not
         * a move. Offering it would give a card that can be dropped on its own
         * column and appear to do something.
         */
        if (candidate.to === from)
            return false;
        return !actor || candidate.actors.includes(actor);
    });
    return rule?.event ?? null;
}
export const RULES = [
    { from: ['DRAFT'], event: 'submit', to: 'NEW', actors: ['admin', 'dispatcher'] },
    { from: ['NEW'], event: 'triage', to: 'TRIAGED', actors: ['admin', 'dispatcher', 'system'] },
    {
        from: ['NEW', 'TRIAGED'],
        event: 'dispatch',
        to: 'DISPATCHING',
        actors: ['admin', 'dispatcher', 'system'],
    },
    {
        from: ['DISPATCHING'],
        event: 'dispatch_failed',
        to: 'DISPATCH_FAILED',
        actors: ['system'],
    },
    // Direct assign — DESIGN.md §4.2. The job is theirs immediately.
    { from: ASSIGNABLE, event: 'assign', to: 'ASSIGNED', actors: ['admin', 'dispatcher'] },
    // Acknowledgement confirms they have seen it; it does not change ownership.
    {
        from: ['ASSIGNED'],
        event: 'acknowledge',
        to: 'ASSIGNED',
        actors: ['technician', 'system'],
        guard: mustBeAssignee,
    },
    {
        from: ['ASSIGNED'],
        event: 'decline',
        to: 'TRIAGED',
        actors: ['technician'],
        guard: (context) => mustBeAssignee(context) ?? requireDeclineReason(context),
    },
    {
        from: ['ASSIGNED'],
        event: 'start_travel',
        to: 'EN_ROUTE',
        actors: ['technician', 'dispatcher', 'admin'],
        guard: (context) => requireAssignee(context) ?? mustBeAssignee(context),
    },
    {
        from: ['ASSIGNED', 'EN_ROUTE'],
        event: 'arrive',
        to: 'ON_SITE',
        actors: ['technician', 'dispatcher', 'admin'],
        guard: (context) => requireAssignee(context) ?? mustBeAssignee(context),
    },
    {
        from: ['ON_SITE'],
        event: 'begin_work',
        to: 'IN_PROGRESS',
        actors: ['technician', 'dispatcher', 'admin'],
        guard: mustBeAssignee,
    },
    {
        from: ['ON_SITE', 'IN_PROGRESS'],
        event: 'complete',
        to: 'WORK_DONE',
        actors: ['technician', 'dispatcher', 'admin'],
        guard: (context) => mustBeAssignee(context) ?? requireOutcome(context),
    },
    /**
     * Closing a child ticket because the incident it belongs to was fixed.
     *
     * A separate event rather than reusing `complete`, and the reason matters: a
     * ticket linked to an incident is never assigned and never visited, so it has
     * no `ON_SITE` to complete from. Forcing it through the normal path would
     * mean writing a site visit that did not happen into an append-only timeline
     * that an SLA report is built from.
     *
     * System-only. A dispatcher closing one ticket individually should use the
     * ordinary route; this exists for the fan-out and nothing else.
     */
    {
        from: [
            'DRAFT',
            'NEW',
            'TRIAGED',
            'DISPATCHING',
            'ASSIGNED',
            'DISPATCH_FAILED',
            'EN_ROUTE',
            'ON_SITE',
            'IN_PROGRESS',
            'PENDING_PARTS',
            'PENDING_CUSTOMER',
            'PENDING_ACCESS',
            'ON_HOLD',
        ],
        event: 'resolve_with_incident',
        to: 'WORK_DONE',
        actors: ['system'],
        guard: requireOutcome,
    },
    { from: ['WORK_DONE'], event: 'verify', to: 'VERIFIED', actors: ['admin', 'dispatcher'] },
    {
        from: ['WORK_DONE', 'VERIFIED'],
        event: 'close',
        to: 'CLOSED',
        actors: ['admin', 'dispatcher', 'system'],
    },
    // Pausing remembers where it came from, so resume is not a guess.
    {
        from: HOLDABLE,
        event: 'hold',
        to: (context) => context.payload.holdStatus ?? 'ON_HOLD',
        destinations: ['ON_HOLD', 'PENDING_PARTS', 'PENDING_CUSTOMER', 'PENDING_ACCESS'],
        actors: ['admin', 'dispatcher', 'technician'],
        guard: (context) => notAlreadyHeld(context) ?? requireReason(context),
    },
    {
        from: ['ON_HOLD', 'PENDING_PARTS', 'PENDING_CUSTOMER', 'PENDING_ACCESS'],
        event: 'resume',
        to: (context) => context.job.heldFromStatus,
        actors: ['admin', 'dispatcher', 'technician'],
        guard: mustKnowWhereToResume,
    },
    {
        from: CANCELLABLE,
        event: 'cancel',
        to: 'CANCELLED',
        actors: ['admin', 'dispatcher'],
        guard: requireReason,
    },
    // Admin only — ROLES.md §2. A dispatcher cannot reopen closed work.
    {
        from: ['CLOSED', 'VERIFIED'],
        event: 'reopen',
        to: 'IN_PROGRESS',
        actors: ['admin'],
        guard: requireReason,
    },
];
/**
 * The only way a job's status may change.
 *
 * Callers persist the returned status *and* an event row in one transaction —
 * a state change without a timeline entry must be impossible (README rule 1).
 */
export function applyTransition(event, context) {
    const { job, actor } = context;
    const matching = RULES.filter((rule) => rule.event === event && rule.from.includes(job.status));
    if (matching.length === 0) {
        return {
            ok: false,
            reason: 'illegal_transition',
            message: `Cannot ${event.replace(/_/g, ' ')} a job that is ${job.status.replace(/_/g, ' ').toLowerCase()}.`,
        };
    }
    // A rule the actor is allowed to use wins; otherwise report the actor problem.
    const permitted = matching.find((rule) => rule.actors.includes(actor.type));
    if (!permitted) {
        return {
            ok: false,
            reason: 'forbidden_actor',
            message: `A ${actor.type} cannot ${event.replace(/_/g, ' ')} this job.`,
        };
    }
    const failure = permitted.guard?.(context);
    if (failure)
        return { ok: false, reason: 'guard_failed', message: failure };
    const to = typeof permitted.to === 'function' ? permitted.to(context) : permitted.to;
    // Entering a pause records where to come back to; leaving one clears it.
    let heldFromStatus = job.heldFromStatus;
    if (isPaused(to) && !isPaused(job.status))
        heldFromStatus = job.status;
    else if (!isPaused(to))
        heldFromStatus = null;
    return { ok: true, from: job.status, to, event, heldFromStatus, statusUnchanged: to === job.status };
}
/** Which events an actor could fire right now — drives the UI's action buttons. */
export function availableEvents(job, actor) {
    return TRANSITION_EVENTS.filter((event) => {
        const result = applyTransition(event, { job, actor, payload: {}, now: new Date() });
        // Guard failures still mean the event is offerable — the UI collects the
        // missing reason or outcome. Illegal and forbidden do not.
        return result.ok || result.reason === 'guard_failed';
    });
}
//# sourceMappingURL=transitions.js.map